// The "Minimum DPI/PPI" sidebar control: read it, and read what the URL says about it.
//
// Its own module because both `navigation.ts` (which builds the loader payload and owns the
// debounced dispatch) and `miscOperations.ts` (which builds the URL) need it, and those two
// already import each other — a third edge would make the cycle load-bearing.
import { getDomElement } from "../utils/dom";
import {
  findPrintSizeSegment,
  formatPrintSizeSegment,
  parsePrintSizeSegment,
} from "@brave-new-world/shared";

/** The ids of the four controls, in the order the form reads them. */
export const PRINT_SIZE_INPUT_IDS = [
  "lm_dpi_height",
  "lm_dpi_width",
  "lm_dpi_dpi",
] as const;

/**
 * The "Minimum DPI/PPI" control as a URL segment, or "" when it is not fully filled in.
 *
 * Empty is the answer for a half-filled control (a height typed but no DPI yet): the user
 * is still typing, and a filter that applied itself mid-keystroke would narrow the grid
 * under them. It is also the answer for out-of-range input, which the shared codec rejects.
 */
export function printSizeSegment(): string {
  const num = (id: string): number =>
    Number(getDomElement<HTMLInputElement>(id)?.value.trim() ?? "");
  const height = num("lm_dpi_height");
  const width = num("lm_dpi_width");
  const dpi = num("lm_dpi_dpi");
  const unit = getDomElement<HTMLSelectElement>("lm_dpi_unit")?.value === "in" ? "in" : "cm";
  if (!height || !width || !dpi) return "";
  const segment = formatPrintSizeSegment({ height, width, unit, dpi });
  // Round-tripped through the parser rather than trusted: `Number("1e3")` is 1000, and a
  // paste can put anything in a text input. If it does not read back, it does not apply.
  return parsePrintSizeSegment(segment) ? segment : "";
}

/** The print-size segment currently in the address bar, or "". */
export function printSizeSegmentInUrl(): string {
  const found = findPrintSizeSegment(location.pathname.split("/"));
  return found ? formatPrintSizeSegment(found) : "";
}


/**
 * Empty the control, unit included.
 *
 * Called by "Clear filters", which otherwise left this one applied — it clears the option
 * hiddens, and those are the only thing it knew about.
 */
export function clearPrintSize(): void {
  for (const id of PRINT_SIZE_INPUT_IDS) {
    const el = getDomElement<HTMLInputElement>(id);
    if (el) el.value = "";
  }
  const unit = getDomElement<HTMLSelectElement>("lm_dpi_unit");
  if (unit) unit.value = "cm";
}
